import type {
  LocationQuery,
  LocationQueryRaw,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationRaw,
} from "vue-router";
import type { AuthQueryParams } from "../types/providers";
import { getHygienaOptions, providerNameToQueryParam, queryParamToProviderName } from "../utils/providers";

export function useAuthQueryParams(to?: RouteLocationNormalizedLoadedGeneric) {
  const queryParamsSnapshot = ref<LocationQuery>({});

  const config = useRuntimeConfig();
  const router = useRouter();

  const authStore = useAuthStore();
  const localePath = useLocalePath();
  const getRouteBaseName = useRouteBaseName();

  // Do not use useRoute here, since it will be used in a middleware and plugin
  const currentRoute = computed(() => to ?? router.currentRoute.value);
  const currentRouteName = computed(() => getRouteBaseName(currentRoute.value));

  const isCurrentRouteRoot = computed(() => currentRouteName.value === "index");
  const isCurrentRouteLogin = computed(() => currentRouteName.value === "login");

  const redirectQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.redirect ?? currentRoute.value.query.redirect;
    return value?.toString();
  });

  /** if the page should open the login automatically */
  const autoLoginQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.autoLogin ?? currentRoute.value.query.autoLogin ?? "";
    return ["true", "1"].includes(value.toString().toLocaleLowerCase());
  });

  const loginHintQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.loginHint ?? currentRoute.value.query.loginHint;
    return value?.toString();
  });

  const providerNameQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.provider ?? currentRoute.value.query.provider ?? "";
    return queryParamToProviderName(value.toString());
  });

  const tokenQueryParam = computed(() => currentRoute.value.query.token);

  const currentQueryParams = computed(() => ({
    redirect: redirectQueryParam.value,
    autoLogin: autoLoginQueryParam.value ? "true" : undefined,
    loginHint: loginHintQueryParam.value,
    provider: providerNameQueryParam.value,
    token: tokenQueryParam.value,
  }));

  const availableApps = computed(() => ({
    sureKleanz: {
      appName: String(config.public.HYGIENA_AUTH_JETSETTER_APP_NAME),
      url: String(config.public.APP_URL_JET_SETTER),
      displayName: "Sure KLEANZ",
    },
    sureKleanzSetup: {
      appName: String(config.public.HYGIENA_AUTH_SELF_SERVICE_APP_NAME),
      url: String(config.public.APP_URL_SETUP),
      displayName: "Sure KLEANZ Setup",
    },
    sureTrend: {
      appName: String(config.public.HYGIENA_AUTH_STC_APP_NAME),
      url: String(config.public.APP_URL_STC),
      displayName: "Sure Trend",
    },
  }));

  type AvailableApp = keyof typeof availableApps.value;

  function getAppInfo(appNameOrKey: string, fallback?: AvailableApp) {
    const result =
      Object.entries(availableApps.value).find(([key, { appName }]) => [key, appName].includes(appNameOrKey)) ??
      (fallback ? [fallback, availableApps.value[fallback]] : undefined);
    if (!result) return;

    const [key, app] = result;
    return {
      ...app,
      key: key as AvailableApp,
    };
  }

  const makeQueryParams = (options: AuthQueryParams = {}) => {
    return {
      ...currentQueryParams.value,
      redirect: options.redirect,
      autoLogin: options.autoLogin ? "true" : undefined,
      loginHint: options.loginHint,
      provider: providerNameToQueryParam(options.provider),
    };
  };

  const signupRoute = computed<RouteLocationRaw>(() => ({
    path: localePath({
      name: "signup",
    }),
    query: makeQueryParams(),
  }));

  const initQueryParams = async () => {
    // Initialize the query params snapshot with the current route's query parameters
    queryParamsSnapshot.value = {
      ...currentRoute.value.query, // Keeps existing query params
    };

    if (Object.keys(queryParamsSnapshot.value).length > 0) {
      devConsole.log(
        "🛠️ Auth page initialized with query params:",
        currentRoute.value.fullPath,
        unref(queryParamsSnapshot.value),
      );
    }
  };

  const navigateToPage = (path: string, query?: LocationQueryRaw) => {
    if (path === currentRoute.value.path) {
      devConsole.error(`🔒 ckAuth: Already on the page: ${path}`);
      return;
    }
    devConsole.info(`🔒 ckAuth: Redirecting to ${path} page from ${currentRoute.value.fullPath}`);

    return navigateTo({
      path: path,
      query,
    });
  };

  const goToAuthorizedPage = (path?: string) => {
    if (!authStore.isAuthed) {
      devConsole.warn(`🔒 ckAuth: User is not authenticated, not redirecting to authorized page ${path}`);
      goToUnauthorizedRedirectPage();
      return;
    }

    const targetPath = localePath(path ?? redirectQueryParam.value ?? "/");
    devConsole.info(`🔒 ckAuth: Redirecting to ${targetPath} from strict route`, currentRoute.value.fullPath);
    return navigateToPage(targetPath);
  };

  const goToUnauthorizedRedirectPage = (path?: string, options?: AuthQueryParams) => {
    const query = makeQueryParams(options);
    if (authStore.isAuthed) {
      devConsole.warn(`🔒 ckAuth: User is authenticated, not redirecting to unauthorized page ${path}`);
      return goToAuthorizedPage();
    }

    const unauthorizedRedirect = localePath(path || String(config.public.UNAUTHORIZED_REDIRECT || "/login"));
    if (unauthorizedRedirect === currentRoute.value.path) {
      devConsole.error(`🔒 ckAuth: Already on the unauthorized page: ${unauthorizedRedirect}`);
      return;
    }

    return navigateToPage(unauthorizedRedirect, query);
  };

  const goToRoot = () => {
    return goToAuthorizedPage("/");
  };

  const goBack = (path?: string) => {
    return navigateToPage(localePath(path ?? "/"));
  };

  const createRedirectPath = ({
    to,
    from,
    ticketId,
    error,
    warn,
    redirect,
  }: {
    to?: string;
    from?: string;
    ticketId?: string;
    error?: string;
    warn?: string;
    redirect?: string | URL;
  } = {}) => {
    const redirectUrl = redirect instanceof URL ? redirect : new URL(redirect ?? "/", window.location.origin);

    const redirectPath = redirectUrl.toString().replace(redirectUrl.origin, "");

    return localePath({
      path: `/redirecting`,
      query: {
        to,
        from,
        ticketId,
        error,
        warn,
        redirect: redirectPath && redirectPath !== "/" ? encodeURIComponent(redirectPath) : undefined,
      },
    });
  };

  const createCrossAppRedirectPath = (
    toApp: string,
    {
      path,
      error,
      warn,
      queryParams,
    }: {
      path?: string;
      error?: string;
      warn?: string;
      queryParams?: Record<string, number | string | undefined | null>;
    } = {},
  ) => {
    const from = getHygienaOptions(config).appName;
    const redirect = new URL(path ?? "/", window.location.origin);
    Object.entries(queryParams ?? {}).forEach(([key, value]) => value && redirect.searchParams.set(key, String(value)));

    return createRedirectPath({
      to: toApp,
      redirect,
      error,
      warn,
      from,
    });
  };

  return {
    isCurrentRouteLogin,
    isCurrentRouteRoot,
    signupRoute,
    autoLoginQueryParam,
    providerNameQueryParam,
    availableApps,
    getAppInfo,
    initQueryParams,
    makeQueryParams,
    goToUnauthorizedRedirectPage,
    goToAuthorizedPage,
    goToRoot,
    goBack,
    createRedirectPath,
    createCrossAppRedirectPath,
  };
}
